import React from 'react';
import PropTypes from 'prop-types';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';

import { lineHeights, fontSizes } from 'style/config';
import { Box, Link, Text } from 'components/ui';
import BccRwdCopy from 'components/Bcc/BccRwdCopy/BccRwdCopy';
import RichText from 'components/Content/RichText';
import Markdown from 'components/Markdown/Markdown';

import localeUtils from 'utils/LanguageLocale';
import keyConsts from 'utils/KeyConstants';
import { DebouncedResize } from 'constants/events';
import isLegacyContentfulMarkdown from 'utils/isLegacyContentfulMarkdown';
import extractContentfulText from 'utils/extractContentfulText';

const getText = localeUtils.getLocaleResourceFile('components/Catalog/locales', 'Catalog');

class SeoText extends BaseClass {
    state = {
        hasOverflow: false,
        showMore: false
    };

    textRef = React.createRef();

    handleResize = () => {
        if (!this.state.showMore) {
            const desc = this.textRef.current;
            this.setState({
                hasOverflow: desc.scrollHeight > desc.offsetHeight
            });
        }
    };

    handleFocusWithin = e => {
        if (!this.state.showMore) {
            switch (e.key) {
                case keyConsts.UP:
                case keyConsts.DOWN:
                case keyConsts.LEFT:
                case keyConsts.RIGHT:
                case keyConsts.TAB:
                    this.toggleOverflow();

                    break;
                default:
                    break;
            }
        }
    };

    componentDidMount() {
        this.handleResize();
        window.addEventListener(DebouncedResize, this.handleResize);
        this.textRef.current.addEventListener('keyup', this.handleFocusWithin);
    }

    componentWillUnmount() {
        window.removeEventListener(DebouncedResize, this.handleResize);
        this.textRef.current.removeEventListener('keyup', this.handleFocusWithin);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.contextId !== this.props.contextId) {
            this.setState({ showMore: false }, this.handleResize);
        }
    }

    toggleOverflow = () => {
        const currentScroll = window.scrollY;
        this.setState({ showMore: !this.state.showMore }, () => {
            window.scroll(0, currentScroll);
        });
    };

    render() {
        const { showMore } = this.state;
        const lineHeight = lineHeights.relaxed * fontSizes.base;

        // text = "SEO Text" field (WYSIWYG), content = "SEO Text Markdown" field
        const { content, text } = this.props;

        // Determine which component to use based on content type and kill switch
        // https://jira.sephora.com/browse/ECS-8861
        let contentComponent;

        // Helper function to render the SEO Text field (handles both legacy markdown and modern WYSIWYG)
        const renderSeoText = () => {
            const hasLegacyMarkdown = isLegacyContentfulMarkdown(text);

            // Could be a scenario where a content producer puts markdown in the SEO Text field
            if (hasLegacyMarkdown) {
                // Legacy: Extract and render markdown from SEO Text field
                const markdownText = extractContentfulText(text);

                return <Markdown content={markdownText} />;
            }

            // Render WYSIWYG content from SEO Text field (How content prodcucers are expected to use it :P)
            return <RichText content={text} />;
        };

        // Get KS Value from C-HUB
        const isSeoTextEnabled = Boolean(Sephora.configurationSettings?.seoText?.isEnabled);

        // Check if content is markdown string (SEO Text Markdown field) vs Markdown object (legacy)
        const hasMarkdownContent = content && typeof content === 'string';

        // Define priority order based on KS value (seoText.isEnabled)
        // When enabled: seoText > markdown > BCC
        // When disabled: markdown > seoText > BCC
        const contentPriority = isSeoTextEnabled ? ['seoText', 'markdown', 'bcc'] : ['markdown', 'seoText', 'bcc'];

        // Render content based on priority order
        for (const source of contentPriority) {
            if (source === 'seoText' && text) {
                contentComponent = renderSeoText();

                break;
            }

            if (source === 'markdown' && hasMarkdownContent) {
                contentComponent = <Markdown content={content} />;

                break;
            }

            if (source === 'bcc') {
                contentComponent = <BccRwdCopy content={content} />;

                break;
            }
        }

        return (
            <>
                <Box
                    ref={this.textRef}
                    lineHeight='relaxed'
                    overflow='hidden'
                    maxWidth='74em'
                    maxHeight={showMore || [lineHeight * 2, lineHeight * 3.5]}
                >
                    {contentComponent}
                </Box>
                {this.state.hasOverflow && (
                    <Text
                        is='p'
                        lineHeight='relaxed'
                    >
                        <Link
                            onClick={this.toggleOverflow}
                            color='blue'
                            padding={2}
                            margin={-2}
                            children={getText(showMore ? 'showLess' : 'showMore')}
                        />
                    </Text>
                )}
            </>
        );
    }
}

SeoText.propTypes = {
    content: PropTypes.oneOfType([PropTypes.object, PropTypes.string]), // BCC (object or string)
    text: PropTypes.object, // Contentful
    contextId: PropTypes.string.isRequired
};

SeoText.defaultProps = {
    content: null,
    text: null
};

export default wrapComponent(SeoText, 'SeoText', true);
